import { useCallback, useMemo } from 'react';

import { getImageUrlsFromContent } from '@/shared/utils/content';

import type { GameAnswer, Question } from '@/features/gameSession/types';

export type LaneChooserQuestionDetails = {
	question?: Question;
	answers: GameAnswer[];
	answerLabels: string[];
	answerCorrectFlags: boolean[];
	titleText: string;
	questionHtml: string;
	imageUrls: string[];
	questionHasImage: boolean;
};

export type SubmitAnswerResult = {
	isCorrect: boolean;
	answer: GameAnswer;
	answerIndex: number;
	elapsedSeconds: number;
};

export type UseLaneChooserQuestionParams = {
	question?: Question;
	questionStartTs: number;
};

const MAX_ANSWERS = 4;

const normalizeAnswers = (answers?: GameAnswer[] | null): GameAnswer[] => {
	if (!Array.isArray(answers)) return [];
	return answers.filter(Boolean).slice(0, MAX_ANSWERS);
};

const extractQuestionHtml = (question?: Question): string => {
	if (!question) return '';
	const rawDisplay = question.question_display;
	if (typeof rawDisplay === 'string') return rawDisplay;
	if (Array.isArray(rawDisplay)) {
		const first = rawDisplay[0];
		if (typeof first === 'string') return first;
	}
	return question.title || '';
};

export default function useLaneChooserQuestion({ question, questionStartTs }: UseLaneChooserQuestionParams): {
	details: LaneChooserQuestionDetails;
	submitAnswer: (selectedIndex: number) => SubmitAnswerResult | null;
} {
	const answers = useMemo(() => normalizeAnswers(question?.answers), [question]);

	const details = useMemo<LaneChooserQuestionDetails>(() => {
		const answerLabels = answers.map((item) => item?.answer_display ?? '');
		const answerCorrectFlags = answers.map((item) => Boolean(item?.is_correct));
		const titleText = String(question?.title ?? '').trim();
		const questionHtml = extractQuestionHtml(question);
		const questionDisplay = question?.question_display;
		const firstDisplayItem = Array.isArray(questionDisplay) ? questionDisplay[0] : questionDisplay;
		const imageUrls = getImageUrlsFromContent(typeof firstDisplayItem === 'string' ? firstDisplayItem : '') ?? [];
		const questionHasImage = imageUrls.length > 0;

		return {
			question,
			answers,
			answerLabels,
			answerCorrectFlags,
			titleText,
			questionHtml,
			imageUrls,
			questionHasImage,
		};
	}, [answers, question]);

	const submitAnswer = useCallback(
		(selectedIndex: number): SubmitAnswerResult | null => {
			const selectedAnswer = answers[selectedIndex];
			if (!selectedAnswer) {
				return null;
			}

			const elapsedSeconds = Math.max(0, Math.round((Date.now() - questionStartTs) / 1000));
			return {
				isCorrect: Boolean(selectedAnswer.is_correct),
				answer: selectedAnswer,
				answerIndex: selectedIndex,
				elapsedSeconds,
			};
		},
		[answers, questionStartTs],
	);

	return { details, submitAnswer };
}
